import { environment } from '../../../environments/environment';
import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UserService } from '../../core';
import { User } from '../../core/user/user';
import { AbstractBaseComponent } from '../abstract-base.component';
import { SessionStateService } from '../../core/user/session-state.service';
import { skip, takeUntil } from 'rxjs/operators';

/**
 * Login page!
 */
@Component({
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent extends AbstractBaseComponent implements OnInit {

    username: string;
    password: string;
    showPrompt: boolean = false;
    loginSubmitted: boolean = false;
    errorMessages: string[] = null;
    warningMessages: string[] = null;
    successMessages: string[] = null;
    //baseUrl: string = BASE_API_URL + "/Account.mvc/LogOn/v2";
    baseUrl: string = environment.base_api_url + "/Account.mvc/LogOn/v2";

    /** Construct */
    constructor(private userSvc: UserService,
                private route: ActivatedRoute,
                private router: Router,
                private sessionState: SessionStateService) {
        super();
    }

    ngOnInit(): void {       
        let snapshot = this.route.snapshot;
        let message = snapshot.params['logout'] || snapshot.queryParams['logout'];
        let jwtKey = snapshot.queryParams['jwt'];

        //For saml login processing you can skip our login page, and go right to saml auth
        //by providing your domain as ?sd=yourdomain
        let samlDomain = this.route.snapshot.queryParamMap.get('sd');
        if (samlDomain) {
            this.loginSubmitted = true;
            console.log("Login via saml domain " + samlDomain);
            let userPromise = this.userSvc.loginViaCredentials('user@' + samlDomain,'saml');
            this.processLoginAttempt(userPromise);
            return;
        }

        // Did we get here via a logout link? Clear session.
        if (message && (typeof message === 'string') && (message as string).length < 6) /*especially if "true" */ {
            message = 'You have logged out.';
            this.sessionState.prepareForUser(null);
        }

        this.userSvc.logout()
            .then(() => this.errorMessages = message ? [message]: undefined)
            .catch(ex => this.errorMessages = [ex.toString()]);

        // Show/hide login prompt based on user login state.
        // Ensures that the login hides quickly during login process. We also use this to not show the prompt
        // when a JWT is passed in. Always calls upon subscribe - skip that one.
        this.userSvc.user$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(user => {
            this.showPrompt = (user == null);
        });

        // Are we pointed to an existing JWT to use?
        if (jwtKey) {
            let jwt = localStorage.getItem(jwtKey);
            if (jwt) {
                this.loginSubmitted = true;
                console.log("Login via received JWT");
                let userPromise = this.userSvc.loginByJWT(jwt);
                this.processLoginAttempt(userPromise);
                return;
            }
            else {
                console.log("No JWT in localStorage at " + jwtKey);
            }
        }

        this.showPrompt = true;
    }

    /**
     * Attempt login.
     */
    login() {
        this.loginSubmitted = true;
        this.errorMessages = null;
        this.warningMessages = null;
        let userNm = this.username ? this.username.trim() : ""; // When User does not enter username, it results in trim on undefined error.
        let userPromise = this.userSvc.loginViaCredentials(userNm, this.password);
        this.processLoginAttempt(userPromise);
    }

    /**
     * Regardless if login via credentials or passed in JWT, process the result from the UserService.
     * @param loginPromise
     */
    private processLoginAttempt(loginPromise: Promise<User>) {
        loginPromise
            .then((user: User) => {
                if (user.ssoRedirect) {
                    window.location.href = user.ssoRedirect; 
                    return; 
                }

                if (this.userSvc.isAuthenticated) {                   
                    // Get the redirect URL from a route parameter.
                    let redirect = this.route.snapshot.queryParams['redirectUrl'] || "/";

                    // Redirect the user
                    return this.router.navigate([redirect]);
                }
                else {
                    throw "Something didn't work. Please try again.";
                }
            })
            .catch((response: any) => {
                console.error(response);
                let json = null;
                
                try{
                    json = response.error;
                }
                catch(notJson){
                    json = { error: 'Unknown', error_description: response}
                }

                if(json && json.error === "Inactivity"){
                    this.warningMessages = (json.error_description) ? [json.error_description] : [json.error];
                }
                else if(json){
                    this.errorMessages = (json.error_description) ? [json.error_description] : [json.error];
                }
                else{
                    let text = response.toString() || response.text.toString();
                    if (text){
                        this.errorMessages = [text];
                    }
                    else{
                        this.errorMessages = [response.statusText];
                    }
                }

                this.userSvc.logout().then(() => this.showPrompt = true);
                this.loginSubmitted = false;
            });
    }

    /**
     * User click the Forgot Password link.
     */
    navigate() {
       window.location.href = this.baseUrl;     
       
    }
}
